import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import {
  OverviewSectionHeader,
  OverviewLabel,
  OverviewTitleComponent,
  OverviewDescriptionComponent,
} from '@/src/components/OverviewSection';
import { transformProperties, fieldHasContent } from '@/src/domains/prismic/helpers';
import { defaultHtmlSerializer } from '@/src/domains/prismic/render';
import { FAQSection } from '@/src/views/Services/FAQSection';

const FAQWrapper = styled(FAQSection)`
  && {
    ${({ $withHeader }) => {
      switch ($withHeader) {
        case true:
          return css`
            background-color: var(--colors-blank);

            ${OverviewSectionHeader} {
              ${fluidProp('padding-top', '100px', '150px')}
              padding-bottom: 25px;

              ${OverviewLabel}, ${OverviewTitleComponent}, ${OverviewDescriptionComponent} {
                display: flex;
                justify-content: flex-start;
                text-align: left;
                margin: unset;
              }
              ${OverviewLabel} {
                color: var(--colors-irisBlue);
                text-transform: uppercase;
              }
              ${OverviewTitleComponent} {
                ${({ theme }) => theme.typography.scale4}
                max-width: unset;
                padding-top: 20px;
              }
              ${OverviewDescriptionComponent} {
                color: var(--colors-lynch);
                max-width: unset;
              }
            }
          `;

        default:
          return css`
            background-color: #f4f8fe;

            ${OverviewSectionHeader} {
              padding: 0;
            }
          `;
      }
    }}
  }
`;

export const NoteWrapper = styled.div`
  background-color: #f4f7fc;
  padding: 20px;
  margin-top: 25px;
  border-radius: 10px;
`;

export const Title = styled.div`
  ${({ theme }) => theme.typography.bodyLeadBold};
  color: var(--colors-irisBlue);

  & > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: inherit;
  }
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.darkBlue_75};
  margin-top: 10px;
`;

export const NoteHeader = styled.div`
  display: flex;
  align-items: center;
`;

export const IconWrapper = styled.div`
  display: flex;
  padding-right: 15px;
`;

export function FaqWithIconsSlice({ slice }) {
  const { primary } = transformProperties(slice);
  const { label, title, description, secondTitle, noteIcon, noteTitle, noteDescription } = primary;
  const { fields: sections } = slice;

  const hasHeading =
    fieldHasContent(label) || fieldHasContent(title) || fieldHasContent(description);

  return (
    <>
      <FAQWrapper
        $withHeader={hasHeading}
        label={label}
        title={title}
        description={description}
        secondTitle={secondTitle}
        frequentlyAskedQuestions={sections}
        htmlSerializer={defaultHtmlSerializer}
        defaultIndex
      >
        <NoteWrapper>
          <NoteHeader>
            {noteIcon && (
              <IconWrapper>
                <Image
                  src={noteIcon?.url}
                  alt={noteIcon?.alt || ''}
                  width={noteIcon?.dimensions.width}
                  height={noteIcon?.dimensions.height}
                  quality="100"
                />
              </IconWrapper>
            )}
            {fieldHasContent(noteTitle) && (
              <Title>
                <PrismicRichText field={noteTitle} />
              </Title>
            )}
          </NoteHeader>
          {fieldHasContent(noteDescription) && (
            <Description>
              <PrismicRichText field={noteDescription} />
            </Description>
          )}
        </NoteWrapper>
      </FAQWrapper>
    </>
  );
}
